import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFlag,
  FaTimes,
  FaExclamationTriangle,
  FaShieldAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaSpinner,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";

const ReportModal = ({ isOpen, onClose, noteId, noteTitle }) => {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    checkDarkMode();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.position = "relative";
      document.body.style.width = "auto";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.position = "relative";
      document.body.style.width = "auto";
    };
  }, [isOpen]);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const reportReasons = [
    { value: "copyright", label: "Copyright Infringement" },
    { value: "inappropriate", label: "Inappropriate Content" },
    { value: "spam", label: "Spam or Misleading" },
    { value: "low_quality", label: "Poor Quality" },
    { value: "duplicate", label: "Duplicate Content" },
    { value: "other", label: "Other" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      toast.error("Please select a reason");
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/reports",
        {
          noteId,
          reason,
          description,
          noteTitle,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        "Report submitted successfully. We will review it shortly.",
      );
      onClose();
      setReason("");
      setDescription("");
    } catch (error) {
      console.error("Report error:", error);
      toast.error("Failed to submit report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.98)" : "rgba(255, 255, 255, 0.98)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (!isOpen) return null;

  // Use React Portal to render modal at document.body level
  return createPortal(
    <AnimatePresence>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          style={{
            width: "500px",
            maxWidth: "90%",
            maxHeight: "85vh",
            backgroundColor: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            overflow: "hidden",
            border: `1px solid ${getBorderColor()}`,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "20px 24px",
              borderBottom: `1px solid ${getBorderColor()}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "rgba(239, 68, 68, 0.2)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFlag color="#ef4444" size={20} />
              </div>
              <div>
                <h2
                  style={{
                    color: "white",
                    margin: 0,
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                  }}
                >
                  Report Content
                </h2>
                <p
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    margin: "4px 0 0 0",
                    fontSize: "0.75rem",
                  }}
                >
                  Help us maintain quality content
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                cursor: "pointer",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.2)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.1)")
              }
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
            {/* Note Info */}
            <div
              style={{
                marginBottom: "20px",
                padding: "12px 16px",
                backgroundColor: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                borderRadius: "12px",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "8px",
                }}
              >
                <FaInfoCircle size={12} color="#8b5cf6" />
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Note Information
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  color: getTextColor(),
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  wordBreak: "break-word",
                }}
              >
                {noteTitle}
              </p>
            </div>

            {/* Reason */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                  fontSize: "0.85rem",
                }}
              >
                Reason for Reporting *
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "0.9rem",
                  outline: "none",
                  backgroundColor: getInputBackground(),
                  color: getTextColor(),
                  cursor: "pointer",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              >
                <option value="">Select a reason</option>
                {reportReasons.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                  fontSize: "0.85rem",
                }}
              >
                Additional Details (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="4"
                placeholder="Please provide any additional details that might help us review this report..."
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "0.9rem",
                  resize: "vertical",
                  fontFamily: "inherit",
                  backgroundColor: getInputBackground(),
                  color: getTextColor(),
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              />
            </div>

            {/* Warning */}
            <div
              style={{
                marginBottom: "20px",
                padding: "12px",
                backgroundColor: "rgba(245, 158, 11, 0.1)",
                borderRadius: "12px",
                border: "1px solid rgba(245, 158, 11, 0.3)",
                display: "flex",
                gap: "12px",
              }}
            >
              <FaExclamationTriangle
                size={16}
                color="#f59e0b"
                style={{ flexShrink: 0 }}
              />
              <div>
                <p
                  style={{
                    margin: 0,
                    color: "#f59e0b",
                    fontSize: "0.75rem",
                    fontWeight: "500",
                  }}
                >
                  False reports may result in account restrictions
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    color: getTextSecondary(),
                    fontSize: "0.7rem",
                  }}
                >
                  Please report only genuine issues.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundColor: isDarkMode
                    ? "rgba(100, 116, 139, 0.3)"
                    : "#f1f5f9",
                  color: getTextColor(),
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundColor: submitting ? "#94a3b8" : "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <FaSpinner
                    style={{ animation: "spin 1s linear infinite" }}
                    size={14}
                  />
                ) : (
                  <FaPaperPlane size={14} />
                )}
                {submitting ? "Submitting..." : "Submit Report"}
              </button>
            </div>

            {/* Footer */}
            <div
              style={{
                marginTop: "20px",
                paddingTop: "16px",
                borderTop: `1px solid ${getBorderColor()}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <FaShieldAlt size={12} color="#10b981" />
              <span style={{ fontSize: "0.65rem", color: getTextSecondary() }}>
                Your report will be reviewed by our moderation team
              </span>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body,
  );
};

export default ReportModal;

